// src/components/OnboardingGuard.jsx
import { useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { useUser } from "../hooks/useRole"

const ONBOARDING_PREFIXES = ["/onboarding/branch", "/onboarding/done", "/onboarding"]

function isOnboardingPath(pathname) {
  return ONBOARDING_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`)
  )
}

export default function OnboardingGuard({ children }) {
  const navigate = useNavigate()
  const { userRole, businessId, loading } = useUser()

  useEffect(() => {
    if (loading || userRole !== "owner" || !businessId) return

    const pathname = window.location.pathname
    if (isOnboardingPath(pathname)) return

    const checkOnboardingStatus = async () => {
      try {
        const { supabase } = await import("../lib/supabase")

        const { data: branches, error: branchError } = await supabase
          .from("branches")
          .select("id")
          .eq("business_id", businessId)
          .eq("is_active", true)
          .limit(1)

        if (branchError) {
          console.error("Error checking branches:", branchError)
          return
        }

        if (!branches?.length) {
          navigate("/onboarding/branch", { replace: true })
          return
        }

        const { data: employees, error } = await supabase
          .from("users")
          .select("id")
          .eq("business_id", businessId)
          .limit(2)

        if (error) {
          console.error("Error checking onboarding status:", error)
          return
        }

        if (!employees || employees.length <= 1) {
          navigate("/onboarding", { replace: true })
        }
      } catch (err) {
        console.error("Error in onboarding check:", err)
      }
    }

    checkOnboardingStatus()
  }, [userRole, businessId, loading, navigate])

  if (loading || userRole !== "owner") {
    return children
  }

  return children
}
